import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Pages
import LandingPage from "@/pages/LandingPage";
import ScannerPage from "@/pages/ScannerPage";
import BuyPage from "@/pages/BuyPage";
import LoginPage from "@/pages/LoginPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import DashboardPage from "@/pages/DashboardPage";
import QRPublicPage from "@/pages/QRPublicPage";
import QRAssignPage from "@/pages/QRAssignPage";
import QREditPage from "@/pages/QREditPage";
import ForgotPINPage from "@/pages/ForgotPINPage";
import AdminDashboard from "@/pages/AdminDashboard";

// 404 Not Found Page
const NotFoundPage = () => {
  return (
    <div className="min-h-screen bg-[#F3F4F6] flex items-center justify-center p-6">
      <div className="text-center max-w-sm">
        <div className="w-20 h-20 bg-[#DC2626]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-3xl font-bold text-[#DC2626]">404</span>
        </div>
        <h2 className="text-xl font-bold text-[#1F2933] mb-2" style={{ fontFamily: 'Manrope' }}>
          Page Not Found
        </h2>
        <p className="text-[#6B7280] mb-6">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <a 
          href="/"
          className="inline-flex items-center justify-center h-12 px-6 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-lg font-medium"
        >
          Go to Scanner
        </a>
      </div>
    </div>
  );
};

// Protected Route using Firebase Auth
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const navigate = useNavigate();
  const { user, userProfile, loading, isAdmin } = useAuth();

  useEffect(() => {
    if (!loading) {
      if (!user) {
        navigate('/login', { replace: true });
        return;
      }
      
      if (requireAdmin && !isAdmin) {
        navigate('/dashboard', { replace: true });
        return;
      }
    }
  }, [user, loading, requireAdmin, isAdmin, navigate]);

  if (loading) {
    return (
      <div className="app-container flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#2563EB] border-t-transparent"></div>
      </div>
    );
  }

  if (!user) return null;
  if (requireAdmin && !isAdmin) return null;

  return children;
};

// App Router
function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/scan" element={<ScannerPage />} />
      <Route path="/buy" element={<BuyPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/admin-login" element={<AdminLoginPage />} />
      <Route path="/q/:code" element={<QRPublicPage />} />
      <Route path="/qr/:code" element={<QRPublicPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/assign/:code" element={
        <ProtectedRoute>
          <QRAssignPage />
        </ProtectedRoute>
      } />
      <Route path="/edit/:code" element={
        <ProtectedRoute>
          <QREditPage />
        </ProtectedRoute>
      } />
      <Route path="/forgot-pin/:code" element={
        <ProtectedRoute>
          <ForgotPINPage />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute requireAdmin>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      {/* Catch-all route for 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="app-container">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
